'use client'

// React Imports
import { useCallback, useRef, useState } from 'react'

// Dialog exit animations (GUI-wide sweep, Luca 2026-07-24): conditional
// mounting — `{target !== null && <XDialog …/>}` — kills MUI's exit
// transition, so dialogs vanished abruptly (the ENTER worked because the
// mount is born with open=true). This hook retains the last non-null target
// while the dialog fades out:
//
//   const edit = useExitTransition(editTarget)
//   {edit.mounted !== null && (
//     <EditDialog row={edit.mounted} open={edit.open} onExited={edit.onExited} … />
//   )}
//
// `mounted` drives the conditional mount, `open` drives the Dialog, and
// `onExited` — wired to the dialog transition (FormDialog forwards it, raw
// Dialogs take it via slotProps.transition) — releases the retained value once
// the fade completes. Form state therefore resets at UNMOUNT, after the fade,
// never during it (the form must not visibly empty while dissolving).
export const useExitTransition = <T,>(target: T | null): { mounted: T | null; open: boolean; onExited: () => void } => {
  const [retained, setRetained] = useState<T | null>(target)
  const targetRef = useRef(target)

  targetRef.current = target

  if (target !== null && target !== retained) {
    // Render-time sync (the React-sanctioned derived-state idiom): a new
    // target must be visible in the same render, not one commit later.
    setRetained(target)
  }

  const onExited = useCallback(() => {
    // Release only when the dialog is still meant to be closed — a reopen
    // racing the fade keeps the mount alive.
    if (targetRef.current === null) {
      setRetained(null)
    }
  }, [])

  return { mounted: target !== null ? target : retained, open: target !== null, onExited }
}

// The boolean-flag variant, for `{open && <XDialog/>}` call sites.
export const useExitFlag = (open: boolean): { mounted: boolean; open: boolean; onExited: () => void } => {
  const { mounted, open: opening, onExited } = useExitTransition<true>(open ? true : null)

  return { mounted: mounted !== null, open: opening, onExited }
}
